import { useState } from "react";
import { Link } from "react-router";
import { Capacitor } from "@capacitor/core";
import { Settings, ShieldAlert } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import {
  canRequestUsagePermission,
  useUsageAccess,
} from "../../lib/native/usageAccess";

export function UsagePermissionGate({
  onCollectUsage,
}: {
  onCollectUsage?: () => Promise<unknown>;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const { permission: livePermission, permissionReady, requiresEntitlement, requestPermission } =
    useUsageAccess();
  const [permissionBusy, setPermissionBusy] = useState(false);
  const isAndroid = Capacitor.getPlatform() === "android";

  const canRequestPermission = canRequestUsagePermission(
    permissionReady,
    livePermission,
    requiresEntitlement,
  );

  async function handleRequestPermission() {
    setPermissionBusy(true);
    try {
      const state = await requestPermission();
      if (state.status === "granted") await onCollectUsage?.();
    } finally {
      setPermissionBusy(false);
    }
  }

  const requestLabel = isAndroid
    ? permissionBusy
      ? t.permission.openingUsageAccess
      : t.permission.openUsageAccess
    : permissionBusy
      ? t.permission.requestingAccess
      : t.permission.requestScreenTimeAccess;

  return (
    <main className="screen0-shell grid min-h-[100svh] content-start gap-6 !pb-[max(18px,env(safe-area-inset-bottom))] !pt-[max(18px,env(safe-area-inset-top))]">
      <div className="flex justify-end">
        <Link className="icon-button" to="/settings" aria-label={t.common.openSettings}>
          <Settings size={20} />
        </Link>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-1 pb-8">
        {requiresEntitlement ? (
          <section className="liquid-glass-alert-warning flex w-full max-w-md gap-3 p-4 text-[oklch(82%_0.055_85)]">
            <ShieldAlert size={20} className="shrink-0" />
            <p className="text-sm leading-6">{t.permission.entitlementMissing}</p>
          </section>
        ) : null}

        <section className="liquid-glass-panel grid w-full max-w-md gap-4 p-5">
          <h2 className="text-xl font-semibold">{t.permission.gateTitle}</h2>
          <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
            {isAndroid ? t.permission.gateBodyAndroid : t.permission.gateBody}
          </p>
          {canRequestPermission ? (
            <button
              type="button"
              className="primary-action w-full disabled:opacity-60"
              disabled={permissionBusy}
              onClick={() => void handleRequestPermission()}
            >
              {requestLabel}
            </button>
          ) : null}
        </section>
      </div>
    </main>
  );
}
